import { useState, useEffect, useCallback } from 'react';
import { Activity, MapPin, AlertOctagon, Clock, Users, CheckCircle2, Zap, TrendingUp } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { CATEGORY_CONFIG } from '../components/lifelink/CategoryGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLOR = { critica: '#FF3B30', alta: '#FF9F0A', media: '#FFD60A', bassa: '#32D74B' };

export default function OperationsTVPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const fetchData = useCallback(async () => {
    try {
      const url = `${API}/public/operations` + (token ? `?token=${token}` : '');
      const { data } = await axios.get(url);
      setData(data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore caricamento dashboard');
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // refresh every 10s
    return () => clearInterval(interval);
  }, [fetchData]);

  // Auto-detect title for window
  useEffect(() => {
    document.title = 'LIFE LINKE · Centrale Operativa TV';
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center text-white p-8">
        <div className="text-center max-w-md">
          <AlertOctagon size={64} className="mx-auto text-[#FF3B30] mb-4" />
          <h1 className="text-2xl font-['Barlow_Condensed'] font-black uppercase tracking-tight">Accesso negato</h1>
          <p className="text-sm text-[#B8D4E8] mt-2">{error}</p>
          <p className="text-xs text-[#6B8AB5] mt-4 uppercase tracking-widest">Aggiungi il token: ?token=XXXX</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center text-white">
        <Activity size={48} className="animate-pulse text-[#FF3B30]" />
      </div>
    );
  }

  const k = data.kpi || {};
  const points = data.heatmap || [];
  const center = points.length > 0
    ? [points.reduce((s, p) => s + p.latitudine, 0) / points.length, points.reduce((s, p) => s + p.longitudine, 0) / points.length]
    : [41.9028, 12.4964]; // Rome default

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white p-4 sm:p-6" data-testid="operations-tv-page">
      {/* Header */}
      <header className="flex items-center justify-between mb-5 pb-4 border-b border-[#1A2D44]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
            <Activity size={26} strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black uppercase tracking-tight leading-none">
              CENTRALE OPERATIVA · LIVE
            </h1>
            <p className="text-xs text-[#B8D4E8] uppercase tracking-widest mt-1">LIFE LINKE · Sala situazione</p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 justify-end">
            <span className="w-2 h-2 bg-[#32D74B] rounded-full animate-pulse"></span>
            <span className="text-xs text-[#32D74B] uppercase tracking-widest font-mono">In tempo reale</span>
          </div>
          <div className="font-['JetBrains_Mono'] text-xl text-white mt-1">{new Date(data.generated_at).toLocaleTimeString('it-IT')}</div>
          <div className="text-[10px] text-[#6B8AB5] uppercase tracking-widest">{new Date(data.generated_at).toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long' })}</div>
        </div>
      </header>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
        <KpiCard label="Critiche Attive" value={k.critiche_attive} color="#FF3B30" Icon={AlertOctagon} pulse={k.critiche_attive > 0} />
        <KpiCard label="Ultima Ora" value={k.last_1h} color="#FF9F0A" Icon={Zap} />
        <KpiCard label="Oggi" value={k.today} color="#FFD60A" Icon={TrendingUp} />
        <KpiCard label="Nuove" value={k.nuove} color="#0A84FF" Icon={Clock} />
        <KpiCard label="In Corso" value={k.in_corso} color="#5E5CE6" Icon={Activity} />
        <KpiCard label="Assegnate" value={k.assegnata} color="#BF5AF2" Icon={Users} />
        <KpiCard label="Risolte Oggi" value={k.risolte_oggi} color="#32D74B" Icon={CheckCircle2} />
        <KpiCard label="Operatori" value={k.operatori_disponibili} color="#64D2FF" Icon={Users} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Map */}
        <div className="lg:col-span-2 bg-[#1A2D44] border border-[#2A4060] overflow-hidden" style={{ minHeight: 420 }}>
          <div className="p-3 border-b border-[#2A4060] flex items-center gap-2">
            <MapPin size={14} className="text-[#FF3B30]" />
            <span className="text-xs font-bold uppercase tracking-widest">Mappa Live · ultime 24h</span>
            <span className="text-[10px] text-[#B8D4E8] ml-auto">{points.length} punti</span>
          </div>
          <div style={{ height: 380 }}>
            <MapContainer center={center} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"
              />
              {points.map((p, i) => (
                <CircleMarker
                  key={i}
                  center={[p.latitudine, p.longitudine]}
                  radius={p.priorita === 'critica' ? 10 : p.priorita === 'alta' ? 7 : 5}
                  pathOptions={{ color: PRIORITY_COLOR[p.priorita] || '#0A84FF', fillColor: PRIORITY_COLOR[p.priorita] || '#0A84FF', fillOpacity: 0.5 }}
                >
                  <Popup>
                    <div className="text-xs">
                      <strong>{p.categoria?.replace(/_/g, ' ').toUpperCase()}</strong><br />
                      Priorità: <span style={{ color: PRIORITY_COLOR[p.priorita] }}>{p.priorita}</span>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Top categories 24h */}
        <div className="bg-[#1A2D44] border border-[#2A4060] p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={14} className="text-[#FF9F0A]" />
            <span className="text-xs font-bold uppercase tracking-widest">Categorie · 24h</span>
          </div>
          {data.categorie_24h?.length === 0 ? (
            <p className="text-xs text-[#6B8AB5] py-4 text-center">Nessuna richiesta nelle ultime 24h</p>
          ) : (
            <div className="space-y-2">
              {data.categorie_24h?.map((c) => {
                const cfg = CATEGORY_CONFIG.find(x => x.id === c.categoria);
                const max = Math.max(...(data.categorie_24h || []).map(x => x.count), 1);
                const pct = (c.count / max) * 100;
                return (
                  <div key={c.categoria} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#E8F4FD] uppercase tracking-wide font-bold">{c.categoria.replace(/_/g, ' ')}</span>
                      <span className="font-mono text-[#FF9F0A] font-bold">{c.count}</span>
                    </div>
                    <div className="h-1.5 bg-[#0D1B2A]">
                      <div className="h-full" style={{ width: `${pct}%`, background: cfg?.color || '#FF9F0A' }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent critical/high requests */}
      <div className="bg-[#1A2D44] border border-[#2A4060]">
        <div className="p-3 border-b border-[#2A4060] flex items-center gap-2">
          <AlertOctagon size={14} className="text-[#FF3B30]" />
          <span className="text-xs font-bold uppercase tracking-widest">Emergenze Critiche e Alte · ultime 24h</span>
          <span className="text-[10px] text-[#B8D4E8] ml-auto">{data.ultime_emergenze?.length || 0}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="operations-recent-table">
            <thead>
              <tr className="bg-[#0D1B2A]">
                <th className="text-left px-3 py-2 text-[10px] text-[#B8D4E8] uppercase tracking-widest">Quando</th>
                <th className="text-left px-3 py-2 text-[10px] text-[#B8D4E8] uppercase tracking-widest">Categoria</th>
                <th className="text-left px-3 py-2 text-[10px] text-[#B8D4E8] uppercase tracking-widest">Priorità</th>
                <th className="text-left px-3 py-2 text-[10px] text-[#B8D4E8] uppercase tracking-widest">Stato</th>
                <th className="text-left px-3 py-2 text-[10px] text-[#B8D4E8] uppercase tracking-widest">Indirizzo</th>
                <th className="text-left px-3 py-2 text-[10px] text-[#B8D4E8] uppercase tracking-widest">Operatore</th>
                <th className="text-left px-3 py-2 text-[10px] text-[#B8D4E8] uppercase tracking-widest">Inoltrata</th>
              </tr>
            </thead>
            <tbody>
              {(data.ultime_emergenze || []).map(r => (
                <tr key={r.id} className="border-t border-[#0D1B2A] hover:bg-[#0D1B2A]/40">
                  <td className="px-3 py-2 text-[10px] font-mono text-[#B8D4E8]">{new Date(r.created_at).toLocaleTimeString('it-IT')}</td>
                  <td className="px-3 py-2 text-xs uppercase font-bold">{r.categoria?.replace(/_/g, ' ')}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: PRIORITY_COLOR[r.priorita], background: `${PRIORITY_COLOR[r.priorita]}25` }}>{r.priorita}</span>
                  </td>
                  <td className="px-3 py-2 text-[10px] text-[#B8D4E8] uppercase tracking-widest">{r.stato}</td>
                  <td className="px-3 py-2 text-[10px] text-[#B8D4E8]">{r.indirizzo || '-'}</td>
                  <td className="px-3 py-2 text-[10px] text-[#B8D4E8]">{r.operatore_assegnato || '—'}</td>
                  <td className="px-3 py-2 text-[10px]">
                    {r.dispatched_service ? (
                      <span className="px-2 py-0.5 bg-[#FF9F0A]/20 text-[#FF9F0A] font-bold uppercase tracking-widest">{r.dispatched_service}</span>
                    ) : <span className="text-[#6B8AB5]">—</span>}
                  </td>
                </tr>
              ))}
              {(data.ultime_emergenze || []).length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-xs text-[#6B8AB5]">Nessuna emergenza critica/alta nelle ultime 24h</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="text-center mt-6 text-[9px] text-[#6B8AB5] uppercase tracking-widest">
        Aggiornamento automatico ogni 10 secondi · LIFE LINKE Operations
      </footer>
    </div>
  );
}

function KpiCard({ label, value, color, Icon, pulse }) {
  return (
    <div className="bg-[#1A2D44] border border-[#2A4060] p-3 relative overflow-hidden">
      {pulse && <div className="absolute top-0 left-0 right-0 h-0.5 animate-pulse" style={{ background: color }}></div>}
      <div className="flex items-center gap-1 mb-1">
        <Icon size={11} style={{ color }} />
        <span className="text-[9px] text-[#B8D4E8] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="font-['JetBrains_Mono'] text-2xl sm:text-3xl font-bold" style={{ color }}>{value ?? 0}</div>
    </div>
  );
}
